import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import { useTranslation } from "react-i18next";
import Draggable from "react-draggable";
import swal from "sweetalert";
import {
  LoadingSpinner,
  isUrdu,
  convertToDateString,
  convertUTCToLocalTime,
  customOption,
  customSingleValue,
} from "../../constants/const";
import { ApiFinance } from "./ApiFinance";
import { GetLanguageString } from "../helper/Components";
import { GetSelectedLanguage } from "../helper/Method";
import { ApiFund } from "./ApiFund";
import { addMember } from "../member/ApiMember";
import SearchAutoComplete from "../shared/SearchAutoComplete";
import SelectUnit from "../shared/SelectUnit";
import { getMinMaxDates, validateTransactionDate } from "./fiscalYearHelper";
import { MdOutlineAddCircleOutline } from "react-icons/md";
import { TbTrash, TbTrashOff } from "react-icons/tb";
import MemberAddressPreview from "./MemberAddressPreview";

const EditFund = ({
  setModal,
  memberName,
  setMemberFunds,
  editValue,
  unitId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const toggle = () => setModal(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [initialDate, setInitialDate] = useState(null);
  const [fundValues, setFundValues] = useState({});
  const [serverError, setServerError] = useState("");
  const [errors, setErrors] = useState({
    date: "",
    month: "",
    year: "",
    receiptNumber: "",
  });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [infaqHeads, setInfaqHeads] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [infaqRows, setInfaqRows] = useState([{ codeId: "", amount: "" }]);

  // Unit selection state
  const [values, setValues] = useState({
    unit: { value: unitId, label: "" },
    includeSubUnit: false,
  });

  // Contributor selection states
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [recipientType, setRecipientType] = useState("rafiq"); // 'rafiq' | 'rafiq_outside' | 'registered' | 'unregistered'
  const [ahbabOptions, setAhbabOptions] = useState([]);
  const [selectedHabib, setSelectedHabib] = useState("");
  const [outsideRafiqOptions, setOutsideRafiqOptions] = useState([]);
  const [selectedOutsideRafiq, setSelectedOutsideRafiq] = useState("");
  const [loadingSearchOutside, setLoadingSearchOutside] = useState(false);
  const [unregisteredHabibName, setUnregisteredHabibName] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [memberId, setMemberId] = useState(0);
  const [loadingViewDoc, setLoadingViewDoc] = useState(false);

  const isAmanat =
    (fundValues.accountTypeCode || "").toLowerCase() === "amanat_account" ||
    (fundValues.accountTypeCode || "").toLowerCase() === "amanat";

  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  const addRow = () => {
    if (isAmanat) return;
    const defaultCodeId = infaqHeads.length > 0 ? infaqHeads[0].value : "";
    setInfaqRows([...infaqRows, { codeId: defaultCodeId, amount: "" }]);
  };

  const removeRow = (index) => {
    if (isAmanat) return;
    if (infaqRows.length > 1) {
      setInfaqRows(infaqRows.filter((_, i) => i !== index));
    }
  };

  const handleRowChange = (index, field, val) => {
    const newRows = [...infaqRows];
    newRows[index][field] = val;
    setInfaqRows(newRows);

    if (errors.rowErrors && errors.rowErrors[index]) {
      const newErrors = { ...errors };
      newErrors.rowErrors[index][field] = "";
      setErrors(newErrors);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadedFile(e.target.files[0]);
    }
  };

  const effectiveUnitId = values.unit?.value || fundValues.unitId || unitId;

  // Autocomplete search for Registered Habib
  useEffect(() => {
    if (selectedHabib && !selectedHabib.includes("/") && effectiveUnitId) {
      setLoadingSearch(true);
      const timeoutId = setTimeout(() => {
        addMember()
          .getUnitParticipantsByType(
            effectiveUnitId,
            false,
            "habib",
            selectedHabib,
          )
          .then((res) => {
            if (res.data) {
              setAhbabOptions(res.data);
            }
            setLoadingSearch(false);
          })
          .catch((err) => {
            console.error(err);
            setLoadingSearch(false);
          });
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedHabib, effectiveUnitId]);

  // Autocomplete search for Outside Rafiq
  useEffect(() => {
    if (selectedOutsideRafiq && !selectedOutsideRafiq.includes("/") && effectiveUnitId) {
      setLoadingSearchOutside(true);
      const timeoutId = setTimeout(() => {
        addMember()
          .getParentHierarchyParticipantsByType(
            effectiveUnitId,
            "rafiq",
            selectedOutsideRafiq,
          )
          .then((res) => {
            if (res.data) {
              setOutsideRafiqOptions(res.data);
            }
            setLoadingSearchOutside(false);
          })
          .catch((err) => {
            console.error(err);
            setLoadingSearchOutside(false);
          });
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedOutsideRafiq, effectiveUnitId]);

  // Load initial transaction data
  useEffect(() => {
    const currentDate = new Date();
    const tempYear = currentDate.getFullYear();
    const tempMonth = currentDate.getMonth() + 1;
    const loadData = async () => {
      try {
        setLoading(true);
        const fundRes = await ApiFund().getById(editValue);
        const fund = fundRes.data;
        const initialUnitId = fund.unitId || unitId;

        setFundValues({
          ...fund,
          date: fund.date ? convertToDateString(fund.date) : null,
          year: fund.year || tempYear,
          month: fund.month || tempMonth,
        });
        setValues((prev) => ({
          ...prev,
          unit: {
            value: initialUnitId,
            label: fund.unitName || "",
          },
        }));
        setSelectedDate(
          fund.date ? new Date(convertUTCToLocalTime(fund.date)) : null,
        );
        setInitialDate(
          fund.date ? new Date(convertUTCToLocalTime(fund.date)) : null,
        );

        if (fund.entries && fund.entries.length > 0) {
          setInfaqRows(
            fund.entries.map((e) => ({
              codeId: parseInt(e.codeId) || "",
              amount: e.amount || "",
              lineMemo: e.lineMemo || "",
            })),
          );
        } else {
          setInfaqRows([{ codeId: fund.codeId || "", amount: fund.amount || "" }]);
        }

        const headRes = await ApiFinance().GetFinanceSubCategoryAccountsByCode("infaq");
        if (headRes?.data?.length > 0) {
          setInfaqHeads(headRes.data);
        }

        // Fetch initial unit members for initial contributor binding
        let initialMembers = [];
        if (initialUnitId) {
          try {
            const memRes = await addMember().getUnitMembersForSelecting(initialUnitId, false);
            initialMembers = memRes?.data || [];
            setMembers(initialMembers);
          } catch (e) {
            console.error("Error loading unit members:", e);
          }
        }

        // Initialize contributor selection based on existing record
        const rawMemberId = fund.memberId ? parseInt(fund.memberId) : 0;
        const displayName = fund.name || memberName || "";

        if (rawMemberId > 0) {
          setMemberId(rawMemberId);
          const foundInUnit = initialMembers.find((m) => m.id === rawMemberId);
          if (foundInUnit) {
            setRecipientType("rafiq");
            setSelectedMember({
              value: foundInUnit.id,
              label: `${foundInUnit.name} - ${foundInUnit.regNo}`,
              name: foundInUnit.name,
              regNo: foundInUnit.regNo,
            });
          } else if (fund.isOutsideRafiq || fund.isRafiq || !fund.isRafiq) {
            setRecipientType("rafiq_outside");
            setSelectedOutsideRafiq(displayName);
          } else {
            setRecipientType("registered");
            setSelectedHabib(displayName);
          }
        } else if (displayName) {
          if (fund.isOutsideRafiq) {
            setRecipientType("rafiq_outside");
            setSelectedOutsideRafiq(displayName);
          } else if (fund.isRafiq) {
            setRecipientType("rafiq");
            setSelectedMember({
              value: 0,
              label: displayName,
              name: displayName.includes(" - ") ? displayName.split(" - ")[0].trim() : displayName.trim(),
            });
          } else {
            setRecipientType("unregistered");
            setUnregisteredHabibName(displayName);
          }
          setMemberId(0);
        } else {
          setRecipientType("rafiq");
        }

        setLoading(false);
      } catch (err) {
        console.error("Error:", err);
        setLoading(false);
      }
    };

    if (editValue) loadData();
  }, [editValue, memberName, unitId]);

  // Load members when unit selection changes by user interaction
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }
    if (!effectiveUnitId) return;

    addMember()
      .getUnitMembersForSelecting(effectiveUnitId, false)
      .then((res) => {
        const list = res?.data || [];
        setMembers(list);
        if (recipientType === "rafiq") {
          if (selectedMember?.value) {
            const found = list.find((m) => m.id === selectedMember.value);
            if (found) {
              setSelectedMember({
                value: found.id,
                label: `${found.name} - ${found.regNo}`,
                name: found.name,
                regNo: found.regNo,
              });
            } else if (list.length > 0) {
              setSelectedMember({
                value: list[0].id,
                label: `${list[0].name} - ${list[0].regNo}`,
                name: list[0].name,
                regNo: list[0].regNo,
              });
              setMemberId(list[0].id);
            } else {
              setSelectedMember(null);
              setMemberId(0);
            }
          } else if (list.length > 0) {
            setSelectedMember({
              value: list[0].id,
              label: `${list[0].name} - ${list[0].regNo}`,
              name: list[0].name,
              regNo: list[0].regNo,
            });
            setMemberId(list[0].id);
          }
        }
      })
      .catch((err) => {
        console.error("Error loading unit members on unit change:", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUnitId]);

  let membersList = [];
  if (members.length !== 0) {
    members.forEach((m) =>
      membersList.push({
        value: m.id,
        label: `${m.name} - ${m.regNo}`,
        name: m.name,
        regNo: m.regNo,
      }),
    );
  }

  const handleChangeMember = (record) => {
    setSelectedMember(record);
    setMemberId(record?.value || 0);
    setErrors((prev) => ({ ...prev, selectedMember: "" }));
  };

  const handleRecipientTypeChange = (e) => {
    const val = e.target.value;
    setRecipientType(val);
    setErrors((prev) => ({
      ...prev,
      selectedMember: "",
      selectedOutsideRafiq: "",
      selectedHabib: "",
      unregisteredHabibName: "",
    }));
    setMemberId(0);

    if (val === "rafiq") {
      setSelectedOutsideRafiq("");
      setSelectedHabib("");
      setUnregisteredHabibName("");
      if (members.length > 0) {
        setSelectedMember({
          value: members[0].id,
          label: `${members[0].name} - ${members[0].regNo}`,
          name: members[0].name,
          regNo: members[0].regNo,
        });
        setMemberId(members[0].id);
      } else {
        setSelectedMember(null);
      }
    } else if (val === "rafiq_outside") {
      setSelectedMember(null);
      setSelectedHabib("");
      setUnregisteredHabibName("");
      setSelectedOutsideRafiq("");
    } else if (val === "registered") {
      setSelectedMember(null);
      setSelectedOutsideRafiq("");
      setUnregisteredHabibName("");
      setSelectedHabib("");
    } else {
      setSelectedMember(null);
      setSelectedHabib("");
      setSelectedOutsideRafiq("");
      setUnregisteredHabibName("");
    }
  };

  const handleOnSelect = (item) => {
    const str = item?.name || "";
    const remove = str.split(" - ").pop();
    const requiredName = str.replace(" - " + remove, "   ");
    setSelectedHabib(requiredName + " - " + remove.replace("/", " "));
    setMemberId(item.id);
    setErrors((prev) => ({ ...prev, selectedHabib: "" }));
    setAhbabOptions([]);
  };

  const handleOnSelectOutsideRafiq = (item) => {
    const str = item?.name || "";
    const remove = str.split(" - ").pop();
    const requiredName = str.replace(" - " + remove, "   ");
    setSelectedOutsideRafiq(requiredName + " - " + remove.replace("/", " "));
    setMemberId(item.id);
    setErrors((prev) => ({ ...prev, selectedOutsideRafiq: "" }));
    setOutsideRafiqOptions([]);
  };

  const handleViewAttachment = (fileId) => {
    if (!fileId) return;
    setLoadingViewDoc(true);
    ApiFinance()
      .ViewAttachmentByFileId(fileId)
      .then((res) => {
        const contentType = res.headers?.["content-type"] || res.headers?.["Content-Type"] || "application/pdf";
        const file = new Blob([res.data], { type: contentType });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
        setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
      })
      .catch((err) => {
        console.error(err);
        swal({ title: "Failed to view document", icon: "error", buttons: "OK" });
      })
      .finally(() => setLoadingViewDoc(false));
  };

  const validate = () => {
    let temp = {};
    temp.month = fundValues.month ? "" : <GetLanguageString props="funds_add_fund_select_month_error" />;
    temp.year = fundValues.year ? "" : <GetLanguageString props="funds_add_fund_select_year_error" />;

    // Contributor validation
    if (recipientType === "rafiq") {
      temp.selectedMember =
        selectedMember && selectedMember.value
          ? ""
          : <GetLanguageString props="common_field_required_error" />;
    } else if (recipientType === "rafiq_outside") {
      temp.selectedOutsideRafiq =
        selectedOutsideRafiq && memberId
          ? ""
          : <GetLanguageString props="common_field_required_error" />;
    } else if (recipientType === "registered") {
      temp.selectedHabib =
        selectedHabib && memberId
          ? ""
          : <GetLanguageString props="common_field_required_error" />;
    } else {
      temp.unregisteredHabibName =
        unregisteredHabibName && unregisteredHabibName.trim() !== ""
          ? ""
          : <GetLanguageString props="common_field_required_error" />;
    }

    // Receipt number is only required if it is NOT digitally generated
    const isDigitalReceipt =
      fundValues.generateDigitalReceipt ||
      (fundValues.receiptNumber && /[a-zA-Z]/.test(fundValues.receiptNumber));

    temp.receiptNumber =
      isDigitalReceipt || fundValues.receiptNumber
        ? ""
        : <GetLanguageString props="common_field_required_error" />;

    const dateError = validateTransactionDate(selectedDate, initialDate);
    if (dateError) {
      temp.date = t(dateError);
    } else {
      temp.date = selectedDate ? "" : <GetLanguageString props="funds_add_fund_date_paid_error" />;
    }

    if (totalAmount >= 5000) {
      temp.address = fundValues.address && fundValues.address.trim() !== ""
        ? ""
        : <GetLanguageString props="common_field_required_error" />;
    } else {
      temp.address = "";
    }

    temp.memo = fundValues.memo && fundValues.memo.trim() !== ""
      ? ""
      : <GetLanguageString props="common_field_required_error" />;

    // Row validation
    let rowErrors = [];
    let hasRowErrors = false;
    infaqRows.forEach((row, index) => {
      let rowErr = { codeId: "", amount: "" };
      if (!row.codeId) {
        rowErr.codeId = <GetLanguageString props="common_field_required_error" />;
        hasRowErrors = true;
      }
      if (!row.amount || row.amount.toString().trim() === "") {
        rowErr.amount = <GetLanguageString props="common_field_required_error" />;
        hasRowErrors = true;
      } else {
        const val = parseFloat(row.amount);
        if (isNaN(val) || val <= 0) {
          rowErr.amount = t("amount_must_be_greater_than_zero");
          hasRowErrors = true;
        }
      }
      rowErrors[index] = rowErr;
    });
    temp.rowErrors = hasRowErrors ? rowErrors : null;

    setErrors((prev) => ({ ...prev, ...temp }));

    const isMainFieldsValid = Object.keys(temp)
      .filter((k) => k !== "rowErrors")
      .every((k) => temp[k] === "");

    return isMainFieldsValid && !hasRowErrors;
  };

  const handleInputChangeDate = (value) => {
    setSelectedDate(value);
    setErrors((prev) => ({
      ...prev,
      date: "",
    }));
  };

  const handleReceiptNumber = (e) => {
    const { value } = e.target;
    setFundValues({
      ...fundValues,
      receiptNumber: value,
    });
    setErrors((prev) => ({
      ...prev,
      receiptNumber: "",
    }));
  };

  const totalAmount = infaqRows.reduce(
    (sum, row) => sum + (parseFloat(row.amount) || 0),
    0,
  );

  const handleSubmit = () => {
    if (validate()) {
      setLoading(true);

      // Resolve final Contributor payload fields
      let finalMemberId = null;
      let finalName = "";
      let isRafiq = true;
      let isOutsideRafiq = false;

      if (recipientType === "rafiq") {
        finalMemberId = selectedMember?.value || memberId || "";
        finalName = selectedMember?.name || (selectedMember?.label ? (selectedMember.label.includes(" - ") ? selectedMember.label.split(" - ")[0].trim() : (selectedMember.label.includes("-") ? selectedMember.label.substring(0, selectedMember.label.indexOf("-")).trim() : selectedMember.label.trim())) : "");
        isRafiq = true;
        isOutsideRafiq = false;
      } else if (recipientType === "rafiq_outside") {
        finalMemberId = memberId || "";
        const outName = selectedOutsideRafiq
          ? (typeof selectedOutsideRafiq === "string" ? selectedOutsideRafiq : (selectedOutsideRafiq.name || selectedOutsideRafiq.label || ""))
          : "";
        finalName = outName.includes(" - ")
          ? outName.split(" - ")[0].trim()
          : (outName.includes("-") ? outName.substring(0, outName.indexOf("-")).trim() : outName.trim());
        isRafiq = true;
        isOutsideRafiq = true;
      } else if (recipientType === "registered") {
        finalMemberId = memberId || "";
        const regName = selectedHabib
          ? (typeof selectedHabib === "string" ? selectedHabib : (selectedHabib.name || selectedHabib.label || ""))
          : "";
        finalName = regName.includes(" - ")
          ? regName.split(" - ")[0].trim()
          : (regName.includes("-") ? regName.substring(0, regName.indexOf("-")).trim() : regName.trim());
        isRafiq = false;
        isOutsideRafiq = false;
      } else {
        finalMemberId = "";
        finalName = unregisteredHabibName ? unregisteredHabibName.trim() : "";
        isRafiq = false;
        isOutsideRafiq = false;
      }

      const formData = new FormData();
      formData.append("Id", editValue);
      formData.append("MemberId", finalMemberId ?? "");
      formData.append("Name", finalName ?? "");
      formData.append("IsRafiq", isRafiq);
      formData.append("IsOutsideRafiq", isOutsideRafiq);
      formData.append("UnitId", values.unit?.value || fundValues.unitId || unitId);
      formData.append(
        "Month",
        selectedDate ? selectedDate.getMonth() + 1 : fundValues.month,
      );
      formData.append(
        "Year",
        selectedDate ? selectedDate.getFullYear() : fundValues.year,
      );
      formData.append("Date", convertToDateString(selectedDate));
      formData.append("Amount", totalAmount);
      formData.append("ReceiptNumber", fundValues.receiptNumber ?? "");
      formData.append("BankAccountId", fundValues.bankAccountId ?? 0);
      formData.append(
        "AccountTransactionId",
        fundValues.accountTransactionId || "",
      );
      formData.append("IncludeSubUnits", false);
      formData.append(
        "GenerateDigitalReceipt",
        fundValues.generateDigitalReceipt || false,
      );
      formData.append("Memo", fundValues.memo || "");
      formData.append("Address", totalAmount >= 5000 ? (fundValues.address || "") : (fundValues.address || ""));

      infaqRows.forEach((row, i) => {
        formData.append(`Entries[${i}].CodeId`, row.codeId);
        formData.append(`Entries[${i}].Amount`, row.amount);
        formData.append(
          `Entries[${i}].LineMemo`,
          row.lineMemo || fundValues.memo || "",
        );
      });

      if (uploadedFile && !isAmanat) {
        formData.append("file", uploadedFile);
      }

      ApiFund()
        .update(editValue, formData)
        .then((res) => {
          if (res?.data?.statusCode === "300" || res?.data?.StatusCode === 300) {
            const warningMsg = t(res?.data?.message || res?.data?.Message) || "A modification request for this transaction is already pending approval.";
            swal({
              title: warningMsg,
              className: GetSelectedLanguage() === "en" ? "" : "ur",
              icon: "warning",
              buttons: "OK",
            }).then(() => {
              setModal(false);
            });
            return;
          }

          const resMsg = res?.data?.Message || res?.data?.message;
          const isModification = resMsg === "funds_infaq_amanat_edit_requested";
          const successTitle = isModification
            ? t("funds_infaq_amanat_edit_requested")
            : t("member_member_detail_fund_edited") || "Fund edited successfully";

          swal({
            title: successTitle,
            className: GetSelectedLanguage() === "en" ? "" : "ur",
            icon: "success",
            buttons: "OK",
          });

          if (typeof onSuccess === "function") {
            onSuccess();
          }
          setModal(false);
        })
        .catch((err) => {
          const rawErr = err?.response?.data;
          let errMsg = "";
          if (rawErr) {
            if (typeof rawErr === "string") {
              errMsg = rawErr;
            } else if (rawErr.msg) {
              errMsg = rawErr.msg;
            } else if (rawErr.message) {
              errMsg = rawErr.message;
            } else {
              errMsg = JSON.stringify(rawErr);
            }
          } else {
            errMsg = err?.message || (typeof err === "string" ? err : "Something went wrong");
          }

          if (errMsg.length < 150) {
            setServerError(errMsg);
          }
          swal({
            title: errMsg,
            icon: "error",
            buttons: "OK",
          });
          console.error(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const isReceiptNoDisabled =
    fundValues.generateDigitalReceipt ||
    (fundValues.receiptNumber && /[a-zA-Z]/.test(fundValues.receiptNumber));

  return (
    <div>
      <Draggable position={position} onDrag={handleDrag} handle=".modal-header" cancel=".no-drag">
        <Modal
          isOpen
          className="inmodal"
          autoFocus={false}
          size="xl"
        >
          <ModalHeader className="modal-title" tag="h4">
            {<GetLanguageString props="edit_infaq" />}
          </ModalHeader>
          {!loading ? (
            <Form>
              <ModalBody className="no-drag">
                {serverError && <h4 className="text-danger">{serverError}</h4>}

                {/* AMANAT ACCOUNT IMMUTABILITY / MODIFICATION BANNER */}
                {isAmanat && (
                  <Row className="mb-3">
                    <Col md="12">
                      <div
                        style={{
                          background: "#E0F2FE",
                          border: "1px solid #7DD3FC",
                          borderLeft: "4px solid #0284C7",
                          borderRadius: "6px",
                          padding: "12px 16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <i className="fa fa-info-circle" style={{ color: "#0284C7", fontSize: "20px" }}></i>
                        <div>
                          <div style={{ fontWeight: "600", fontSize: "13px", color: "#0369A1" }}>
                            <GetLanguageString props="amanat_account" /> &mdash; <GetLanguageString props="infaq" />
                          </div>
                          <div style={{ fontSize: "12px", color: "#0C4A6E", marginTop: "2px" }}>
                            {t("amanat_amount_locked_notice")}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* SECTION 1: UNIT SELECTION */}
                <Row className="align-items-center mb-2">
                  <SelectUnit
                    values={values}
                    setValues={setValues}
                    unitId={values.unit?.value || unitId}
                    filterUnitTypes={["Usra", "UsraMuavin", "Munfarid"]}
                  />
                </Row>

                <hr style={{ margin: "12px 0 16px 0" }} />

                {/* SECTION 2: CONTRIBUTOR TYPE SELECTOR (RADIO GROUP) */}
                <Row className="mb-3">
                  <Col md={12}>
                    <FormGroup style={{ margin: 0 }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
                        <FormGroup check inline style={{ margin: 0 }}>
                          <Label check>
                            <Input
                              type="radio"
                              name="recipientType"
                              value="rafiq"
                              checked={recipientType === "rafiq"}
                              onChange={handleRecipientTypeChange}
                            />{" "}
                            {<GetLanguageString props="rafiq" />}
                          </Label>
                        </FormGroup>
                        <FormGroup check inline style={{ margin: 0 }}>
                          <Label check>
                            <Input
                              type="radio"
                              name="recipientType"
                              value="rafiq_outside"
                              checked={recipientType === "rafiq_outside"}
                              onChange={handleRecipientTypeChange}
                            />{" "}
                            {<GetLanguageString props="rafiq_outside_nazm" />}
                          </Label>
                        </FormGroup>
                        <FormGroup check inline style={{ margin: 0 }}>
                          <Label check>
                            <Input
                              type="radio"
                              name="recipientType"
                              value="registered"
                              checked={recipientType === "registered"}
                              onChange={handleRecipientTypeChange}
                            />{" "}
                            {<GetLanguageString props="registered_habib" />}
                          </Label>
                        </FormGroup>
                        <FormGroup check inline style={{ margin: 0 }}>
                          <Label check>
                            <Input
                              type="radio"
                              name="recipientType"
                              value="unregistered"
                              checked={recipientType === "unregistered"}
                              onChange={handleRecipientTypeChange}
                            />{" "}
                            {<GetLanguageString props="unregistered_habib" />}
                          </Label>
                        </FormGroup>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                {/* SECTION 3: TRANSACTION MAIN FIELDS (ROW 1: Contributor, Memo, Date) */}
                <Row className="gy-3">
                  {/* CONTRIBUTOR SELECTOR FIELD BASED ON RECIPIENT TYPE */}
                  <Col md="4">
                    <FormGroup>
                      {recipientType === "rafiq" && (
                        <>
                          <Label className="col-form-label">
                            {<GetLanguageString props="funds_select_member" />}
                          </Label>
                          <Select
                            components={{
                              Option: customOption,
                              SingleValue: customSingleValue,
                            }}
                            value={selectedMember}
                            options={membersList}
                            className="basic-single no-drag"
                            classNamePrefix="select"
                            onChange={handleChangeMember}
                            placeholder={t("funds_select_member")}
                          />
                          {errors.selectedMember && (
                            <h4 className="text-error">{errors.selectedMember}</h4>
                          )}
                        </>
                      )}

                      {recipientType === "rafiq_outside" && (
                        <>
                          <Label className="col-form-label">
                            {<GetLanguageString props="funds_select_member" />}
                          </Label>
                          <SearchAutoComplete
                            members={outsideRafiqOptions}
                            setMembers={setOutsideRafiqOptions}
                            setName={setSelectedOutsideRafiq}
                            name={selectedOutsideRafiq}
                            type={"rafiq"}
                            loading={loadingSearchOutside}
                            placeholder={t("common_search_by_name")}
                            handleOnSelect={handleOnSelectOutsideRafiq}
                            calledFrom="mudaris"
                          />
                          {errors.selectedOutsideRafiq && (
                            <h4 className="text-error">{errors.selectedOutsideRafiq}</h4>
                          )}
                        </>
                      )}

                      {recipientType === "registered" && (
                        <>
                          <Label className="col-form-label">
                            {<GetLanguageString props="member_search_habib" />}
                          </Label>
                          <SearchAutoComplete
                            members={ahbabOptions}
                            setMembers={setAhbabOptions}
                            setName={setSelectedHabib}
                            name={selectedHabib}
                            type={"habib"}
                            loading={loadingSearch}
                            placeholder={t("common_search_by_name")}
                            handleOnSelect={handleOnSelect}
                            calledFrom="mudaris"
                          />
                          {errors.selectedHabib && (
                            <h4 className="text-error">{errors.selectedHabib}</h4>
                          )}
                        </>
                      )}

                      {recipientType === "unregistered" && (
                        <>
                          <Label className="col-form-label">
                            {<GetLanguageString props="common_name" />}
                          </Label>
                          <Input
                            type="text"
                            value={unregisteredHabibName}
                            onChange={(e) => {
                              setUnregisteredHabibName(e.target.value);
                              setErrors((prev) => ({ ...prev, unregisteredHabibName: "" }));
                            }}
                            placeholder={t("common_name")}
                          />
                          {errors.unregisteredHabibName && (
                            <h4 className="text-error">{errors.unregisteredHabibName}</h4>
                          )}
                        </>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="transactions_memo" />}
                      </Label>
                      <Input
                        type="text"
                        className={isUrdu(fundValues.memo || "") ? "ur" : ""}
                        value={fundValues.memo || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFundValues({ ...fundValues, memo: val });
                          if (val && val.trim() !== "") {
                            setErrors((prev) => ({ ...prev, memo: "" }));
                          }
                        }}
                        placeholder="Memo..."
                        invalid={!!errors.memo}
                      />
                      {errors.memo && (
                        <h4 className="text-error">{errors.memo}</h4>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="funds_add_fund_date_paid" />}
                      </Label>
                      <DatePicker
                        placeholderText="dd/mm/yyyy"
                        className="form-control"
                        dateFormat="dd/MM/yyyy"
                        selected={selectedDate}
                        minDate={getMinMaxDates(selectedDate, initialDate).minDate}
                        maxDate={getMinMaxDates(selectedDate, initialDate).maxDate}
                        onChange={handleInputChangeDate}
                      />
                      {errors.date && <h4 className="text-error">{errors.date}</h4>}
                    </FormGroup>
                  </Col>

                  {/* ROW 2: Receipt Number, Total Amount */}
                  <Col md="4">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="funds_receipt_number" />}
                      </Label>
                      <Input
                        type="text"
                        value={fundValues.receiptNumber || ""}
                        onChange={handleReceiptNumber}
                        disabled={isReceiptNoDisabled}
                        placeholder={isReceiptNoDisabled ? "Digitally generated" : "Receipt number"}
                      />
                      {isReceiptNoDisabled && (
                        <small className="text-info d-block mt-1" style={{ fontSize: "11px", fontWeight: "bold" }}>
                          <i className="fa fa-info-circle" style={{ marginRight: "4px" }}></i>
                          {<GetLanguageString props="funds_digitally_generated_receipt" />}
                        </small>
                      )}
                      {errors.receiptNumber && (
                        <h4 className="text-error">{errors.receiptNumber}</h4>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="finance_other_income_total_amount" />}
                      </Label>
                      <Input
                        type="text"
                        disabled
                        value={totalAmount}
                      />
                    </FormGroup>
                  </Col>

                  {totalAmount >= 5000 && (
                    <Col md="4">
                      <FormGroup>
                        <Label className="col-form-label">
                          {<GetLanguageString props="member_address" />} <span className="text-danger">*</span>
                        </Label>
                        <MemberAddressPreview
                          memberId={
                            recipientType === "rafiq"
                              ? selectedMember?.value || fundValues.memberId
                              : (recipientType === "registered" || recipientType === "rafiq_outside")
                                ? memberId || fundValues.memberId
                                : fundValues.memberId
                          }
                          totalAmount={totalAmount}
                        />
                        <Input
                          type="text"
                          className={isUrdu(fundValues.address || "") ? "ur" : ""}
                          value={fundValues.address || ""}
                          onChange={(e) =>
                            setFundValues({ ...fundValues, address: e.target.value })
                          }
                          placeholder={t("member_address")}
                        />
                        {errors.address && <h4 className="text-error">{errors.address}</h4>}
                      </FormGroup>
                    </Col>
                  )}

                  {/* DEPOSIT SLIP ATTACHMENT CARD */}
                  {isAmanat ? (
                    fundValues.fileId && (
                      <Col md="12">
                        <div
                          style={{
                            background: "#F8FAFC",
                            padding: "14px 18px",
                            borderRadius: "8px",
                            border: "1px solid #E2E8F0",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <i className="fa fa-file-text-o text-primary" style={{ fontSize: "18px" }}></i>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: "13px", color: "#1E293B" }}>
                                {<GetLanguageString props="deposit_slip" />}
                              </div>
                              <span className="badge bg-success-light text-success" style={{ padding: "3px 6px", borderRadius: "4px", fontSize: "11px", fontWeight: 600 }}>
                                <i className="fa fa-check-circle" style={{ marginRight: "4px" }}></i>
                                {<GetLanguageString props="deposit_slip_already_attached" />}
                              </span>
                            </div>
                          </div>
                          <Button
                            color="white"
                            size="sm"
                            style={{
                              padding: "6px 14px",
                              fontSize: "12px",
                              fontWeight: "500",
                              borderRadius: "6px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              border: "1px solid #CBD5E1",
                            }}
                            disabled={loadingViewDoc}
                            onClick={() => handleViewAttachment(fundValues.fileId)}
                          >
                            {loadingViewDoc ? (
                              <Spinner size="sm" style={{ width: "12px", height: "12px" }} />
                            ) : (
                              <i className="fa fa-eye text-primary"></i>
                            )}
                            <GetLanguageString props="member_add_rafiq_request_view_document" />
                          </Button>
                        </div>
                      </Col>
                    )
                  ) : (
                    /* CASH IN HAND: Optional attachment upload */
                    <Col md="12">
                      <FormGroup style={{ background: "#F9FAFB", padding: "14px 16px", borderRadius: "8px", border: "1px dashed #D1D5DB" }}>
                        <Label className="col-form-label" style={{ fontWeight: 600, color: "#374151" }}>
                          {<GetLanguageString props="deposit_slip_attachment" />}
                        </Label>
                        <Input
                          type="file"
                          onChange={handleFileChange}
                          accept="image/*,application/pdf"
                          style={{ fontSize: "13px" }}
                        />
                        {fundValues.fileId && !uploadedFile && (
                          <div style={{ marginTop: "8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span className="badge bg-success-light text-success" style={{ padding: "4px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600 }}>
                              <i className="fa fa-check-circle" style={{ marginRight: "4px" }}></i>
                              {<GetLanguageString props="deposit_slip_already_attached" />}
                            </span>
                            <Button
                              color="white"
                              size="sm"
                              style={{ padding: "4px 10px", fontSize: "11px", border: "1px solid #CBD5E1" }}
                              disabled={loadingViewDoc}
                              onClick={() => handleViewAttachment(fundValues.fileId)}
                            >
                              <i className="fa fa-eye text-primary me-1"></i>
                              <GetLanguageString props="member_add_rafiq_request_view_document" />
                            </Button>
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  )}

                  {/* MULTI ROW ENTRIES IN IBOX STYLE */}
                  <Col md="12">
                    <div className="ibox mt-3" style={{ border: "1px solid #E7EAEC", borderRadius: "4px" }}>
                      <div className="ibox-title d-flex justify-content-between align-items-center" style={{ background: "#F3F3F4", borderBottom: "1px solid #E7EAEC", padding: "10px 15px" }}>
                        <h5 style={{ margin: 0, fontSize: "14px", fontWeight: 700 }}>
                          {<GetLanguageString props="finance_infaq_entry_details" />}
                        </h5>
                        {!isAmanat && (
                          <MdOutlineAddCircleOutline
                            size={25}
                            color="#3ba147"
                            onClick={addRow}
                            style={{ cursor: "pointer" }}
                          />
                        )}
                      </div>
                      <div className="ibox-content" style={{ padding: "15px" }}>
                        {infaqRows.map((row, index) => (
                          <Row key={index} className="mb-3 align-items-start animate fadeIn">
                            <Col md>
                              <FormGroup>
                                <Label className="col-form-label">
                                  {<GetLanguageString props="fund_account_head" />}
                                </Label>
                                <Select
                                  options={infaqHeads}
                                  value={infaqHeads.find((x) => x.value === row.codeId) || null}
                                  placeholder="Select infaq head"
                                  onChange={(selected) => handleRowChange(index, "codeId", selected ? selected.value : "")}
                                  className="basic-single no-drag"
                                  classNamePrefix="select"
                                />
                                {errors.rowErrors?.[index]?.codeId && (
                                  <h4 className="text-error">{errors.rowErrors[index].codeId}</h4>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md>
                              <FormGroup>
                                <Label className="col-form-label">
                                  {<GetLanguageString props="funds_add_fund_amount" />}
                                </Label>
                                <Input
                                  type="text"
                                  className="no-drag"
                                  disabled={isAmanat}
                                  value={row.amount || ""}
                                  onChange={(e) => {
                                    if (isAmanat) return;
                                    let val = e.target.value.replace(/\D/g, "");
                                    handleRowChange(index, "amount", val);
                                  }}
                                />
                                {errors.rowErrors?.[index]?.amount && (
                                  <h4 className="text-error">{errors.rowErrors[index].amount}</h4>
                                )}
                              </FormGroup>
                            </Col>
                            {!isAmanat && (
                              <Col
                                md="1"
                                className="text-center"
                              >
                                <Label className="col-form-label d-block">&nbsp;</Label>
                                {infaqRows.length === 1 ? (
                                  <TbTrashOff
                                    size={25}
                                    color="#a72424"
                                    style={{ opacity: 0.5 }}
                                  />
                                ) : (
                                  <TbTrash
                                    size={25}
                                    color="#bb0e0e"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => removeRow(index)}
                                  />
                                )}
                              </Col>
                            )}
                          </Row>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button color="white" onClick={toggle} style={{ borderRadius: "6px" }}>
                  {<GetLanguageString props="common_cancel" />}
                </Button>
                <Button color={isAmanat ? "warning" : "primary"} onClick={handleSubmit} style={{ borderRadius: "6px" }}>
                  {isAmanat
                    ? <GetLanguageString props="submit_modification_request" />
                    : <GetLanguageString props="common_save_changes" />}
                </Button>
              </ModalFooter>
            </Form>
          ) : (
            LoadingSpinner()
          )}
        </Modal>
      </Draggable>
    </div>
  );
};

export default EditFund;
